import { BackLink, FormBox, PeriodInput, SiteHeader } from "@/components/ui-kit"
import { useNavigate } from "react-router-dom"
import { Card } from "@/components/ui-kit/card"
import { useEnrichedContracts } from "@/hooks/useEnrichedContracts"
import { useCustomerProfile } from "@/hooks/useCustomerProfile"
import { useMemo, useRef, useState } from "react"
import { ContractsMiniGroup } from "@/components/contracts/contracts-mini-group"
import { RadioGroup } from "@/components/ui/radio-group"
import { Facility } from "@/types/warehouse"
import { Button } from "@/components/ui-kit/button"
import { ArrowRightIcon } from "@/components/icons"
import { Loader2 } from "lucide-react"
import { addDays, addMonths, addWeeks, max, startOfDay, subDays } from "date-fns"
import { useShowcasePrices } from "@/hooks/prices/useShowcasePrices"
import { useContractMutations } from "@/hooks/contracts/useContracts"
import { RENT_PERIOD_OPTIONS, toRentDateString, type RentPeriod } from "@/components/rent/rent-constants"
import { formatDateShort } from "@/lib/utils"
import { isRentInvoice } from "@/lib/invoice-utils"
import { isLegalAddress } from "@/lib/contract-utils"
import type { Contract } from "@/types/contract"

const ADVANCE_PERIODS = RENT_PERIOD_OPTIONS.filter(option => option.value !== "4w")

/** The first day neither paid nor already invoiced: a prepayment continues from there. */
function advanceStart(contract: Contract): Date {
  const covered = [
    contract.paidUntil,
    ...(contract.invoices ?? [])
      .filter(invoice => isRentInvoice(invoice) && invoice.status !== "canceled")
      .map(invoice => invoice.to),
  ]
    .filter((value): value is string => Boolean(value))
    .map(value => new Date(value))

  const today = startOfDay(new Date())
  if (covered.length === 0) {
    return today
  }

  const next = addDays(startOfDay(max(covered)), 1)
  return next > today ? next : today
}

/** Last day (inclusive) of `period` counted from `start`. */
function advanceEnd(start: Date, period: RentPeriod): Date {
  const count = Number.parseInt(period, 10)
  const end = period.endsWith("w") ? addWeeks(start, count) : addMonths(start, count)
  return subDays(end, 1)
}

export function AdvancePayPage() {
  const navigate = useNavigate()

  const { contracts, isLoading, failed, retry } = useEnrichedContracts()
  const { profile } = useCustomerProfile()
  const { requestInvoice } = useContractMutations()
  const customerPhone = profile?.phone ?? ""
  const [phone, setPhone] = useState<string>()
  const [selectedContractId, setSelectedContractId] = useState("")
  const [selectedPeriod, setSelectedPeriod] = useState<RentPeriod>(ADVANCE_PERIODS[0].value)
  const periodSectionRef = useRef<HTMLDivElement>(null)

  const selectedContract = contracts.find(contract => String(contract.id) === selectedContractId)
  const periodStart = useMemo(
    () => (selectedContract ? advanceStart(selectedContract) : null),
    [selectedContract],
  )
  const periodEnd = periodStart ? advanceEnd(periodStart, selectedPeriod) : null

  // The box's current tariff: what the invoice will be issued at. A legal address
  // has no product code, so asking for it as a box finds no tariff.
  const legalAddress = selectedContract ? isLegalAddress(selectedContract) : false
  const productCode = legalAddress ? undefined : selectedContract?.unitType
  const prices = useShowcasePrices(
    {
      productKind: legalAddress ? "legal_address" : "unit",
      facility: selectedContract?.facility,
      productCode,
      startedAt: periodStart ? toRentDateString(periodStart) : undefined,
      showcase: ADVANCE_PERIODS.map(option => option.value),
    },
    { enabled: Boolean(selectedContract?.facility && periodStart && (legalAddress || productCode)) },
  )

  const handleContractSelect = (contractId: string) => {
    setSelectedContractId(contractId)

    requestAnimationFrame(() => {
      periodSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    })
  }
  const pricePlaceholder = !selectedContract
    ? "Выберите бокс"
    : prices.isLoading ? "Считаем стоимость…" : "Стоимость уточнит менеджер"
  const paymentPeriods = ADVANCE_PERIODS.map(option => ({
    value: option.value,
    label: option.label,
    price: prices.data?.items.find(item => item.period === option.value)?.total ?? pricePlaceholder,
  }))
  const selectedPeriodLabel = ADVANCE_PERIODS.find(option => option.value === selectedPeriod)?.label

  const handleRequestInvoice = () => {
    if (!selectedContract || !periodStart || !periodEnd) {
      return
    }

    const contactPhone = (phone ?? customerPhone).trim()
    requestInvoice.mutate(
      {
        contractId: selectedContract.id,
        data: {
          from: toRentDateString(periodStart),
          to: toRentDateString(periodEnd),
          comment: [
            `Оплата заранее: ${selectedPeriodLabel}`,
            contactPhone ? `Телефон для связи: ${contactPhone}` : null,
          ].filter(Boolean).join(". "),
        },
      },
      { onSuccess: () => navigate("/home", { state: { showAdvancePaySuccess: true } }) },
    )
  }

  const groupedContracts = useMemo(() => {
    return contracts.reduce<Record<string, typeof contracts>>((acc, contract) => {
      const facilityCode = contract.facility

      if (contract.status !== 'active') {
        return acc
      }

      if (!acc[facilityCode]) {
        acc[facilityCode] = []
      }

      acc[facilityCode].push(contract)
      return acc
    }, {})
  }, [contracts])

  const hasSelectableContracts = Object.keys(groupedContracts).length > 0
  const emptyMessage = contracts.length === 0
    ? "У вас пока нет боксов"
    : "Сейчас нет боксов, доступных для предоплаты"

  return (
    <div className="min-h-screen bg-background sm:pt-20 pt-12">
      <SiteHeader />
      
      <main className="mx-auto w-full max-w-[722px] px-2 pt-8 pb-16 md:px-4">
        <section className="sm:px-0 px-4">
          <BackLink onClick={() => navigate('/home')} className="sm:mb-4 mb-[6px]">
            <span>Все боксы</span>
          </BackLink>
          <h1 className="mb-6 sm:text-[48px] text-[28px] leading-[36px] sm:leading-[52px] font-semibold text-foreground">
            Оплатить заранее
          </h1>
        </section>

        <section>
          <Card className="mb-6 sm:px-6 px-4 py-6">
            <h2 className="mb-6 sm:text-[28px] text-[24px] sm:leading-[34px] leading-[30px] font-semibold text-foreground">
              Выберите бокс
            </h2>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="size-8 animate-spin text-brand" />
              </div>
            ) : failed ? (
              <div className="flex flex-col gap-6">
                <p className="sm:text-lg text-[16px] sm:leading-[26px] leading-[22px] text-muted-foreground">Не удалось загрузить боксы</p>
                <Button type="button" variant="secondary" className="w-full sm:w-auto" onClick={retry}>
                  Повторить
                </Button>
              </div>
            ) : hasSelectableContracts ? (
              <RadioGroup
                value={selectedContractId}
                onValueChange={handleContractSelect}
                className="flex flex-col sm:gap-6 gap-2"
              >
                {Object.entries(groupedContracts).map(([facilityCode, contracts]) => (
                  <ContractsMiniGroup
                    key={facilityCode}
                    facility={contracts[0].facilityItem as Facility}
                    contracts={contracts}
                  />
                ))}
              </RadioGroup>
            ) : (
              <div className="flex flex-col gap-6">
                <p className="sm:text-lg text-[16px] sm:leading-[26px] leading-[22px] text-muted-foreground">{emptyMessage}</p>
                <Button
                  type="button"
                  variant="primary"
                  className="w-full sm:w-auto"
                  endIcon={<ArrowRightIcon size={24} />}
                  onClick={() => navigate(contracts.length === 0 ? "/rent" : "/home")}
                >
                  {contracts.length === 0 ? "Арендовать бокс" : "Вернуться к боксам"}
                </Button>
              </div>
            )}
          </Card>

          {hasSelectableContracts ? (
            <>
              <div ref={periodSectionRef} className="scroll-mt-24 sm:scroll-mt-28">
                <Card className="sm:mb-6 mb-2 sm:px-6 px-4 py-6">
                  <h2 className="mb-2 sm:text-[28px] text-[24px] sm:leading-[34px] leading-[30px] font-semibold text-foreground">
                    Выберите срок для оплаты заранее
                  </h2>
                  <div className="mb-6 max-w-[474px] sm:text-lg text-[16px] sm:leading-[26px] leading-[22px] text-muted-foreground">
                    Мы выставим счет на сайте и отправим счет на почту{" "}
                    {profile?.email ? <span className="text-brand">{profile.email}</span> : "клиента"}. Если у нас появятся вопросы — позвоним вам по телефону.
                  </div>

                  <PeriodInput
                    periods={paymentPeriods}
                    value={selectedPeriod}
                    onValueChange={value => setSelectedPeriod(value as RentPeriod)}
                    minLabel={ADVANCE_PERIODS[0].label}
                    maxLabel={ADVANCE_PERIODS[ADVANCE_PERIODS.length - 1].label}
                  />
                  {periodStart && periodEnd ? (
                    <div className="mb-4 sm:text-lg text-[16px] sm:leading-[26px] leading-[22px] text-muted-foreground">
                      Счет на период с {formatDateShort(periodStart.toISOString())} по {formatDateShort(periodEnd.toISOString())}
                    </div>
                  ) : null}
                  <FormBox
                    key={customerPhone || "phone-empty"}
                    id="phone"
                    label="Телефон"
                    type="tel"
                    defaultValue={customerPhone}
                    onChange={event => setPhone(event.target.value)}
                  />
                </Card>
              </div>

              <Button
                type="button"
                className="h-[48px] w-full sm:h-auto sm:text-lg text-[16px] leading-6"
                endIcon={requestInvoice.isPending ? <Loader2 className="size-6 animate-spin" /> : <ArrowRightIcon size={24} />}
                disabled={!selectedContract || requestInvoice.isPending}
                onClick={handleRequestInvoice}
              >
                Запросить счет
              </Button>
            </>
          ) : null}
        </section>
      </main>
    </div>
  )
}